/**
 * Invoice email renderer.
 *
 * NOTE (billing presentation CP2): honors data.displayAmountOnly — when true,
 * the line-items table hides the Qty + Rate columns (Description + line total
 * only). The EDGE-FUNCTION MIRRORS (process-scheduled-tasks
 * renderInvoiceEmailInline + renderInvoiceReminderEmail) reached parity in CP2b.
 * Recurring auto-sent invoices remain itemized in practice only because their
 * display_amount_only is false by construction until the recurring CP.
 *
 * Pure function — takes an InvoiceEmailData snapshot and returns a
 * self-contained subject + HTML body suitable for passing to sendEmail().
 *
 * Mirrors the structure of quoteEmail.ts intentionally. The line-items
 * rendering is duplicated rather than extracted to a shared helper; if
 * a third email type appears we revisit. Two copies isn't an abstraction.
 *
 * Substantive differences from the quote renderer:
 *   - Subject: "Invoice <n> from <workspace>"
 *   - Tone: asks for payment, not approval
 *   - Due-date line is load-bearing — leads the body
 *   - Optional partial-payment block when amount_paid > 0
 *
 * Like the quote renderer, this is self-contained HTML — there is no
 * public invoice view yet, no PDF, no Stripe payment link wired. The
 * email IS the invoice.
 */

import { lineTotal } from './totals'

export interface InvoiceEmailLineItem {
  name: string
  description: string
  qty: number
  unit_price: number
}

export interface InvoiceEmailData {
  /** Invoice number for the subject and header. */
  invoiceNumber: string
  /** Recipient's display name, used in the greeting. */
  contactName: string
  /** Workspace name, used as the sender's friendly identity in the body. */
  workspaceName: string
  /** Line items. */
  lineItems: InvoiceEmailLineItem[]
  /** Currency code (e.g., 'USD'). */
  currency: string
  /** Computed subtotal before tax. */
  subtotal: number
  /** Tax rate as a percentage (e.g., 8.5 for 8.5%). */
  taxRate: number
  /** Computed tax amount. */
  taxAmount: number
  /** Final total. */
  total: number
  /** Optional discount amount (already subtracted from totals). */
  discountAmount?: number | null
  /** Amount already paid. If > 0, partial-payment block renders. */
  amountPaid?: number | null
  /** Outstanding balance. Computed by caller; if null/undefined, falls back to total. */
  amountDue?: number | null
  /** Optional due-date string (ISO). When absent, body says "due upon receipt." */
  dueDate?: string | null
  /** Issue date string (ISO). */
  issueDate: string
  /** Optional notes from the workspace owner. */
  notes?: string | null
  /** Workspace brand color (hex, e.g. '#ff6b00'). Falls back to platform purple #7c3aed when null/undefined. */
  brandColor?: string | null
  /** Workspace logo URL (public Supabase Storage URL). Renders as <img> in the header right slot when present. */
  logoUrl?: string | null
  /** Presentation-only (billing CP2): when true, the line-items table hides the
   *  Qty + Rate columns, rendering Description + line total only. Absent/false =
   *  full itemized output (unchanged). Never affects totals math. */
  displayAmountOnly?: boolean
}

export interface InvoiceEmailRender {
  subject: string
  html: string
}

/** Currency formatter — Intl.NumberFormat ported inline, available in Deno too. */
function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount || 0)
}

/** Date formatter — readable, no time component. */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  } catch {
    return iso
  }
}

/** Minimal HTML escape for user-supplied strings inserted into the body. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function renderInvoiceEmail(data: InvoiceEmailData): InvoiceEmailRender {
  const subject = `Invoice ${data.invoiceNumber} from ${data.workspaceName}`

  // Billing presentation CP2: when true, hide the per-line Qty + Rate columns
  // (header cells + body cells). Totals block below is unaffected either way.
  const amountOnly = !!data.displayAmountOnly

  // Line item rows. Per-line total via the shared lineTotal (Slice 2). Note:
  // InvoiceEmailLineItem carries no tiers, so this is flat (qty × unit_price) =
  // current behavior; the SUBTOTAL/TOTAL rows below use the stored data.subtotal/
  // data.total (already correct for tiered). A tier-aware email breakdown is a
  // follow-up (the email line type + builder would need to carry pricing_type/tiers).
  const itemRows = data.lineItems.map(item => {
    const itemTotal = lineTotal(item)
    return `
      <tr>
        <td style="padding:12px 8px; border-bottom:1px solid #eee; vertical-align:top;">
          <div style="font-weight:600; color:#111;">${esc(item.name || 'Item')}</div>
          ${item.description
            ? `<div style="font-size:13px; color:#666; margin-top:2px;">${esc(item.description)}</div>`
            : ''}
        </td>
        ${amountOnly ? '' : `<td style="padding:12px 8px; border-bottom:1px solid #eee; text-align:right; vertical-align:top; white-space:nowrap;">
          ${item.qty}
        </td>
        <td style="padding:12px 8px; border-bottom:1px solid #eee; text-align:right; vertical-align:top; white-space:nowrap;">
          ${formatCurrency(item.unit_price, data.currency)}
        </td>
        `}<td style="padding:12px 8px; border-bottom:1px solid #eee; text-align:right; vertical-align:top; white-space:nowrap; font-weight:600;">
          ${formatCurrency(itemTotal, data.currency)}
        </td>
      </tr>
    `
  }).join('')

  // Totals block — only render rows that have non-zero values.
  const totalsRows: string[] = []

  totalsRows.push(`
    <tr>
      <td style="padding:6px 8px; text-align:right; color:#666;">Subtotal</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${formatCurrency(data.subtotal, data.currency)}</td>
    </tr>
  `)

  if (data.discountAmount && data.discountAmount > 0) {
    totalsRows.push(`
      <tr>
        <td style="padding:6px 8px; text-align:right; color:#666;">Discount</td>
        <td style="padding:6px 8px; text-align:right; white-space:nowrap;">−${formatCurrency(data.discountAmount, data.currency)}</td>
      </tr>
    `)
  }

  if (data.taxRate && data.taxRate > 0) {
    totalsRows.push(`
      <tr>
        <td style="padding:6px 8px; text-align:right; color:#666;">Tax (${data.taxRate}%)</td>
        <td style="padding:6px 8px; text-align:right; white-space:nowrap;">${formatCurrency(data.taxAmount, data.currency)}</td>
      </tr>
    `)
  }

  totalsRows.push(`
    <tr>
      <td style="padding:10px 8px; text-align:right; font-weight:700; font-size:16px; border-top:2px solid #111;">Total</td>
      <td style="padding:10px 8px; text-align:right; white-space:nowrap; font-weight:700; font-size:16px; border-top:2px solid #111;">
        ${formatCurrency(data.total, data.currency)}
      </td>
    </tr>
  `)

  // Partial-payment block — only renders when amount_paid > 0. Defensive
  // for the edge case where a deposit was recorded before formal invoicing.
  const amountPaid = data.amountPaid || 0
  const amountDue = data.amountDue ?? data.total
  const partialPaymentRows = amountPaid > 0 ? `
    <tr>
      <td style="padding:6px 8px; text-align:right; color:#666;">Already paid</td>
      <td style="padding:6px 8px; text-align:right; white-space:nowrap; color:#0a8055;">−${formatCurrency(amountPaid, data.currency)}</td>
    </tr>
    <tr>
      <td style="padding:10px 8px; text-align:right; font-weight:700; font-size:16px; color:${data.brandColor || '#7c3aed'};">Amount due</td>
      <td style="padding:10px 8px; text-align:right; white-space:nowrap; font-weight:700; font-size:16px; color:${data.brandColor || '#7c3aed'};">
        ${formatCurrency(amountDue, data.currency)}
      </td>
    </tr>
  ` : ''

  // Due-date line is load-bearing. When present, lead with it; when absent,
  // fall back to "due upon receipt" — matches the in-app convention.
  const dueText = data.dueDate
    ? `This invoice is due on <strong>${formatDate(data.dueDate)}</strong>.`
    : `Payment is due upon receipt.`

  // Top-of-body greeting and meta.
  const greeting = `Hi ${esc(data.contactName.split(' ')[0] || data.contactName)},`
  const intro = `Please find your invoice from <strong>${esc(data.workspaceName)}</strong> below.`

  // Notes section — optional.
  const notesBlock = data.notes && data.notes.trim()
    ? `
      <div style="margin-top:24px; padding:16px; background:#f7f7f9; border-radius:8px;">
        <div style="font-size:12px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:6px;">Notes</div>
        <div style="font-size:14px; color:#333; line-height:1.6; white-space:pre-wrap;">${esc(data.notes)}</div>
      </div>
    `
    : ''

  // Email body. Inline styles only — many email clients strip <style> blocks.
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${esc(subject)}</title>
</head>
<body style="margin:0; padding:0; background:#f5f5f7; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <div style="max-width:600px; margin:0 auto; padding:24px 16px;">

    <div style="background:#fff; border-radius:12px; padding:32px; box-shadow:0 1px 3px rgba(0,0,0,0.06);">

      <div style="display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:24px; padding-bottom:20px; border-bottom:1px solid #eee;">
        <div>
          <div style="font-size:12px; font-weight:700; color:${data.brandColor || '#7c3aed'}; text-transform:uppercase; letter-spacing:0.06em; margin-bottom:4px;">Invoice</div>
          <div style="font-size:22px; font-weight:800; color:#111;">${esc(data.invoiceNumber)}</div>
          <div style="font-size:13px; color:#666; margin-top:4px;">Issued ${formatDate(data.issueDate)}</div>
        </div>
        ${data.logoUrl ? `<img src="${esc(data.logoUrl)}" alt="" style="max-height:40px; max-width:120px;" />` : ''}
      </div>

      <p style="margin:0 0 8px; font-size:15px; color:#333;">${greeting}</p>
      <p style="margin:0 0 16px; font-size:15px; color:#333;">${intro}</p>
      <p style="margin:0 0 16px; font-size:15px; color:#333;">${dueText}</p>

      <table cellpadding="0" cellspacing="0" style="width:100%; border-collapse:collapse; margin-top:24px; font-size:14px;">
        <thead>
          <tr style="background:#f7f7f9;">
            <th style="padding:10px 8px; text-align:left; font-size:11px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.04em;">Item</th>
            ${amountOnly ? '' : `<th style="padding:10px 8px; text-align:right; font-size:11px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.04em;">Qty</th>
            <th style="padding:10px 8px; text-align:right; font-size:11px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.04em;">Price</th>
            `}<th style="padding:10px 8px; text-align:right; font-size:11px; font-weight:700; color:#666; text-transform:uppercase; letter-spacing:0.04em;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemRows || `
            <tr>
              <td colspan="${amountOnly ? 2 : 4}" style="padding:24px 8px; text-align:center; color:#999; font-style:italic;">No line items</td>
            </tr>
          `}
        </tbody>
      </table>

      <table cellpadding="0" cellspacing="0" style="width:100%; max-width:280px; margin-left:auto; margin-top:16px; border-collapse:collapse; font-size:14px;">
        ${totalsRows.join('')}
        ${partialPaymentRows}
      </table>

      ${notesBlock}

      <p style="margin:32px 0 0; font-size:13px; color:#666; padding-top:20px; border-top:1px solid #eee;">
        Questions about this invoice? Just reply to this email.
      </p>

    </div>

    <p style="margin:16px 0 0; text-align:center; font-size:12px; color:#999;">
      Sent by ${esc(data.workspaceName)} via Orkestra
    </p>

  </div>
</body>
</html>
  `.trim()

  return { subject, html }
}
